import { Button } from '@/components/ui/button';
import { FileSpreadsheet, FileText } from 'lucide-react';
import { ProjectData } from '@/types/project';
import { exportToCSV } from '@/utils/export/csv';
import { exportToPDF } from '@/utils/pdfExport';
import { useToast } from '@/hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ExportButtonsProps {
  data: ProjectData;
}

export function ExportButtons({ data }: ExportButtonsProps) {
  const { toast } = useToast();

  const handleExportCSV = () => {
    try {
      exportToCSV(data);
      toast({
        title: 'Success',
        description: 'Report exported to CSV successfully.',
        variant: 'default',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error
          ? error.message
          : 'Failed to export CSV report. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleExportPDF = async () => {
    try {
      console.log('Exporting data to PDF:', data); // Debug log
      await exportToPDF(data);
      toast({
        title: 'Success',
        description: 'Report exported to PDF successfully.',
        variant: 'default',
      });
    } catch (error) {
      console.error('PDF Export failed:', error); // Debug log
      toast({
        title: 'Error',
        description: error instanceof Error
          ? error.message
          : 'Failed to export PDF report. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              onClick={handleExportCSV}
              aria-label="Export to CSV"
            >
              <FileSpreadsheet className="h-5 w-5" aria-hidden="true" />
              Export CSV
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Export your data to a CSV file.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              onClick={handleExportPDF}
              aria-label="Export to PDF"
            >
              <FileText className="h-5 w-5" aria-hidden="true" />
              Export PDF
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Export your data to a PDF file.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
